import { fmtDate } from "@/lib/api";
import { Bullets, SubTitle } from "@/components/analysis/resultUi";

const LABELS = {
  how: "Come", when: "Quando", frequency: "Frequenza", avoid: "Cosa non fare", check_after: "Cosa controllare dopo", goal: "Obiettivo",
  where: "Dove", signs: "Cosa cercare", limits: "Limiti", target: "Contro", role: "Ruolo", what_to_do: "Cosa fare",
  conditions: "Quando è appropriato", incompatibilities: "Incompatibilità", expected: "Evoluzione attesa",
  trigger: "Quando intervenire di nuovo", why: "Perché",
};
const ORIGIN = { spontaneo: "Spontaneo: da proteggere", introducibile: "Introducibile", entrambi: "Spontaneo e introducibile" };

export function Item({ item, title, testid }) {
  const rows = Object.entries(item).filter(([k, v]) => v && LABELS[k]);
  return (
    <div className="rounded-xl bg-[#f8f3e9] p-4" data-testid={testid}>
      {title && <p className="text-[17px] font-semibold leading-snug text-[#0D2517]">{title}</p>}
      {rows.map(([k, v]) => (
        <p key={k} className="mt-1 text-[16px] leading-relaxed text-[#2D3A30]"><span className="font-semibold text-[#5b6b60]">{LABELS[k]}:</span> {v}</p>
      ))}
    </div>
  );
}

export const Items = ({ items, prefix, titleKey = "what" }) => (
  <div className="space-y-3">{items.map((it, i) => <Item key={i} item={it} title={it[titleKey]} testid={`${prefix}-${i}`} />)}</div>
);

export const Note = ({ children, testid }) => <p className="text-[16px] italic leading-relaxed text-[#5b6b60]" data-testid={testid}>{children}</p>;

export const mgmtOf = (consult, level) => (consult.management || []).find((m) => m.level === level) || { applicable: false, items: [] };

export function MgmtBlock({ m, title, prefix, titleKey }) {
  return (
    <>
      {title && <SubTitle>{title}</SubTitle>}
      {m.applicable ? <Items items={m.items} prefix={prefix} titleKey={titleKey} /> : m.note && <Note testid={`${prefix}-na`}>{m.note}</Note>}
    </>
  );
}

export function BioItem({ it, i }) {
  return (
    <Item testid={`consult-bio-${i}`} item={{ ...it, organism: undefined, origin: undefined }} title={
      <span><span className="italic">{it.organism}</span>{ORIGIN[it.origin] && <span className="ml-2 inline-block rounded-full bg-[#15803D]/10 px-2.5 py-0.5 text-[13px] font-semibold not-italic text-[#15803D]">{ORIGIN[it.origin]}</span>}</span>
    } />
  );
}

export function Substance({ s, i }) {
  return (
    <div className="rounded-xl border border-[#ddc69c]/70 p-4" data-testid={`consult-substance-${i}`}>
      <p className="text-[17px] font-semibold text-[#0D2517]">{s.name} <span className="text-[14px] font-normal text-[#8a958d]">({s.iso})</span></p>
      {[["why", "Perché è pertinente"], ["target", "Contro"], ["timing", "Fase o momento"], ["ipm_role", "Ruolo nella gestione integrata"], ["considerations", "Considerazioni"], ["limitations", "Limitazioni e precauzioni"]]
        .filter(([k]) => s[k]).map(([k, l]) => <p key={k} className="mt-1 text-[16px] leading-relaxed text-[#2D3A30]"><span className="font-semibold text-[#5b6b60]">{l}:</span> {s[k]}</p>)}
      {s.user_class?.text && <p className="mt-2 text-[15px] leading-relaxed text-[#2D3A30]" data-testid={`consult-substance-registry-${i}`}>{s.user_class.text}</p>}
      {s.registry?.source_name && <p className="mt-1 text-[14px] text-[#8a958d]">Fonte: {s.registry.source_name}{s.registry.dataset_date && ` · aggiornato al ${fmtDate(s.registry.dataset_date)}`}</p>}
    </div>
  );
}

export function VerifiedUse({ u, i }) {
  return (
    <div className="rounded-xl border border-[#15803D]/40 bg-[#15803D]/5 p-4" data-testid={`consult-verified-use-${i}`}>
      <p className="text-[14px] font-semibold uppercase tracking-wide text-[#15803D]">Impiego verificato su fonte ufficiale</p>
      <p className="mt-1 text-[17px] font-semibold text-[#0D2517]">{u.active_substance}</p>
      <p className="text-[16px] text-[#2D3A30]">Coltura: {u.crop}{u.adversity_name && ` · Avversità: ${u.adversity_name}`}</p>
      {u.limitations && <p className="text-[16px] text-[#2D3A30]">Limitazioni: {u.limitations}</p>}
      {u.organic_allowed && <p className="text-[16px] text-[#2D3A30]">Ammesso in agricoltura biologica{u.organic_reference && ` (${u.organic_reference})`}</p>}
      <p className="mt-1 text-[14px] text-[#8a958d]">Fonte: {u.source_url ? <a className="underline" href={u.source_url} target="_blank" rel="noreferrer">{u.source_name}</a> : u.source_name}{u.verified_at && ` · verificato il ${fmtDate(u.verified_at)}`}</p>
    </div>
  );
}

export const Signs = ({ title, items }) => (items?.length > 0 ? <><SubTitle>{title}</SubTitle><Bullets items={items} /></> : null);
